import { useState } from "react";
import { motion } from "framer-motion";
import { Play, User, Users, Dice5, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PLAYER_CONFIGS } from "@/utils/snakeLadderData";
import { savePlayerNames, loadPlayerNames } from "@/utils/playerStorage";

const SnakeLadderSettings = ({ onStartGame, onBack }) => {
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState(() => loadPlayerNames());

  const handleStart = () => {
    savePlayerNames(playerNames);
    const names = playerNames.slice(0, playerCount).map((n, i) => n.trim() || PLAYER_CONFIGS[i].name);
    onStartGame(playerCount, names);
  };

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="glass-card-strong rounded-2xl p-4 sm:p-6 w-full max-w-lg mx-auto"
      data-testid="snake-ladder-settings"
    >
      <div className="flex items-center justify-center gap-2 mb-3">
        <Dice5 className="w-7 h-7 text-gold" />
        <h2 className="font-fredoka text-xl sm:text-2xl text-gold font-bold">Ular Tangga Pintar</h2>
      </div>

      <p className="font-fredoka text-gold-light/80 text-sm text-center mb-4">
        Lempar dadu, jalan di papan, jawab pertanyaan untuk bonus langkah!
      </p>

      {/* Rules */}
      <div className="glass-card rounded-xl p-3 mb-4">
        <div className="grid grid-cols-2 gap-2 text-center font-fredoka text-xs text-gold-light/60">
          <div>🪜 <span className="text-green-400">Tangga</span> = naik</div>
          <div>🐍 <span className="text-red-400">Ular</span> = turun</div>
          <div>❓ <span className="text-gold">Pertanyaan</span> = bonus</div>
          <div>🏆 Sampai <span className="text-gold">100</span> = menang!</div>
        </div>
      </div>

      {/* Player Count */}
      <div className="mb-4">
        <label className="font-fredoka text-gold-light/80 text-sm mb-2 block font-bold">Jumlah Pemain</label>
        <div className="grid grid-cols-4 gap-2">
          {[1, 2, 3, 4].map(n => (
            <Button key={n}
              data-testid={`sl-player-count-${n}`}
              variant={playerCount === n ? "default" : "outline"}
              onClick={() => setPlayerCount(n)}
              className={`font-fredoka font-bold rounded-xl ${playerCount === n
                ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-0 shadow-lg"
                : "glass-card border-gold/20 text-gold hover:bg-gold/10"
              }`}>
              {n === 1 ? <><User className="w-3 h-3 mr-1" />Solo</> : <><Users className="w-3 h-3 mr-1" />{n}P</>}
            </Button>
          ))}
        </div>
      </div>

      {/* Player Names with Characters */}
      <div className="mb-4">
        <label className="font-fredoka text-gold-light/80 text-sm mb-2 block font-bold">Pemain & Karakter</label>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: playerCount }).map((_, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="glass-card rounded-xl p-2.5 border border-gold/10"
              style={{ borderColor: `${PLAYER_CONFIGS[i].color}30` }}
            >
              {/* Character avatar */}
              <div className="flex items-center gap-2 mb-2">
                <motion.div
                  className="w-10 h-10 rounded-full flex items-center justify-center border-2 text-xl"
                  style={{
                    backgroundColor: `${PLAYER_CONFIGS[i].color}20`,
                    borderColor: `${PLAYER_CONFIGS[i].color}60`,
                    boxShadow: `0 0 10px ${PLAYER_CONFIGS[i].color}30`,
                  }}
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
                >
                  {PLAYER_CONFIGS[i].charEmoji}
                </motion.div>
                <div>
                  <p className="font-fredoka text-xs font-bold" style={{ color: PLAYER_CONFIGS[i].color }}>
                    {PLAYER_CONFIGS[i].charName}
                  </p>
                  <p className="font-fredoka text-[10px] text-gold-light/40">Pemain {i + 1}</p>
                </div>
              </div>
              <Input
                data-testid={`sl-player-name-${i}`}
                value={playerNames[i]}
                onChange={(e) => { const n = [...playerNames]; n[i] = e.target.value; setPlayerNames(n); }}
                placeholder={PLAYER_CONFIGS[i].name}
                className="glass-card border-gold/20 text-gold placeholder:text-gold-light/40 font-fredoka rounded-lg focus:border-gold/40 h-9 text-sm"
                maxLength={15}
              />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Buttons */}
      <Button
        onClick={handleStart}
        className="w-full bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-fredoka font-bold uppercase tracking-wide rounded-xl h-12 text-base shadow-lg mb-2"
        data-testid="sl-start-btn"
      >
        <Play className="w-5 h-5 mr-2" />
        {playerCount > 1 ? `Mulai Bermain Ber-${playerCount}!` : "Mulai Bermain!"}
      </Button>

      <Button
        onClick={onBack}
        variant="outline"
        className="w-full glass-card border-gold/20 text-gold hover:bg-gold/10 font-fredoka rounded-xl"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />Kembali ke Menu Utama
      </Button>
    </motion.div>
  );
};

export default SnakeLadderSettings;
